// src/pages/ContactDataPage.tsx
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSave, FiCheckCircle, FiPhone, FiMail, FiMapPin, FiHome, FiUser, FiCalendar, FiFlag, FiFileText, FiShield } from 'react-icons/fi';
import { useAuth, User } from '../context/AuthContext';
import styles from './ProfilePage.module.css';
import { ProfileService, UpdateMePayload } from '../services/profile.service';

interface CompleteProfileForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  state: string;
  postal_code: string;
  date_of_birth: string;
  nationality: string;
  rfc: string;
}

const ContactDataPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, refreshMe } = useAuth();

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  
  // Estados para los checkboxes de políticas
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [dataConsentAccepted, setDataConsentAccepted] = useState(false);

  // Initialize form with user data, ensuring all fields are populated
  const [form, setForm] = useState<CompleteProfileForm>({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    address: user?.address || '',
    city: user?.city || '',
    country: user?.country || 'México',
    state: user?.state || '',
    postal_code: user?.postal_code || '',
    date_of_birth: user?.date_of_birth || '',
    nationality: user?.nationality || 'Mexicana',
    rfc: user?.rfc || ''
  });

  // Update form when user data changes
  React.useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        email: user.email || '',
        address: user.address || '',
        city: user.city || '',
        country: user.country || 'México',
        state: user.state || '',
        postal_code: user.postal_code || '',
        date_of_birth: user.date_of_birth || '',
        nationality: user.nationality || 'Mexicana',
        rfc: user.rfc || ''
      });
    }
  }, [user]);

  const missingRequired = useMemo(() => {
    const firstNameOk = !!form.first_name?.trim();
    const lastNameOk = !!form.last_name?.trim();
    const phoneOk = !!form.phone?.trim();
    const addressOk = !!form.address?.trim();
    const cityOk = !!form.city?.trim();
    const countryOk = !!form.country?.trim();
    const stateOk = !!form.state?.trim();
    const postalCodeOk = !!form.postal_code?.trim();
    const dateOfBirthOk = !!form.date_of_birth?.trim();
    const nationalityOk = !!form.nationality?.trim();
    const rfcOk = !!form.rfc?.trim();
    const policiesOk = privacyAccepted && dataConsentAccepted;
    return !(firstNameOk && lastNameOk && phoneOk && addressOk && cityOk && countryOk && stateOk && postalCodeOk && dateOfBirthOk && nationalityOk && rfcOk && policiesOk);
  }, [form, privacyAccepted, dataConsentAccepted]);

  const handleSave = async () => {
    if (missingRequired) {
      setMsg({ type: 'info', text: 'Todos los campos marcados con * son obligatorios, incluyendo la aceptación de políticas' });
      return;
    }

    setSaving(true);
    setMsg(null);

    try {
      const updateData: UpdateMePayload = {
        first_name: form.first_name?.trim(),
        last_name: form.last_name?.trim(),
        phone: form.phone?.trim(),
        country: form.country?.trim(),
        state: form.state?.trim(),
        city: form.city?.trim(),
        address: form.address?.trim(),
        postal_code: form.postal_code?.trim(),
        date_of_birth: form.date_of_birth?.trim(),
        nationality: form.nationality?.trim(),
        rfc: form.rfc?.trim(),
      };

      await ProfileService.updateMe(updateData);
      
      setMsg({ type: 'success', text: t('common.changes_saved') });

      setTimeout(() => {
        refreshMe();
        setMsg(null);
      }, 2000);

    } catch (error: any) {
      console.error('Error updating profile:', error);
      setMsg({ 
        type: 'error', 
        text: error?.response?.data?.message || error?.message || 'Error al guardar los cambios' 
      });
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className={styles.pageContainer}>
        <div>Cargando perfil...</div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.pageHeader}>
        <div className={styles.titleWrap}>
          <div className={styles.titleAccent}></div>
          <div>
            <h1 className={styles.pageTitle}>Datos de Contacto</h1>
            <p className={styles.subTitle}>
              Actualiza tu información de contacto y datos personales
            </p>
          </div>
        </div>
      </div>

      <section className={styles.profileFormCard}>
        <div className={styles.formContent}>
          <div className={styles.formSection}>
            <h3 className={styles.subsectionTitle}>
              <FiUser /> Información Personal
            </h3>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Nombres <span className={styles.required}>*</span></label>
                <div className={styles.inputWrap}>
                  <FiUser className={styles.leadingIcon} />
                  <input
                    className={styles.field}
                    value={form.first_name || ''}
                    onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                    placeholder="Tu nombre"
                    autoComplete="given-name"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Apellidos <span className={styles.required}>*</span></label>
                <div className={styles.inputWrap}>
                  <FiUser className={styles.leadingIcon} />
                  <input
                    className={styles.field}
                    value={form.last_name || ''}
                    onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                    placeholder="Tu apellido"
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Fecha de Nacimiento <span className={styles.required}>*</span></label>
                <div className={styles.inputWrap}>
                  <FiCalendar className={styles.leadingIcon} />
                  <input
                    type="date"
                    className={styles.field}
                    value={form.date_of_birth || ''}
                    onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))}
                    autoComplete="bday"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Nacionalidad <span className={styles.required}>*</span></label>
                <div className={styles.inputWrap}>
                  <FiFlag className={styles.leadingIcon} />
                  <input
                    className={styles.field}
                    value={form.nationality || ''}
                    onChange={e => setForm(f => ({ ...f, nationality: e.target.value }))}
                    placeholder="Mexicana"
                    autoComplete="country"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.subsectionTitle}>
              <FiMail /> Datos de Contacto
            </h3>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Email</label>
                <div className={styles.inputWrap}>
                  <FiMail className={styles.leadingIcon} />
                  <input
                    className={`${styles.field} ${styles.readonlyField}`}
                    value={form.email || ''}
                    readOnly
                    autoComplete="email"
                  />
                </div>
                <small className={styles.hint}>
                  El email no se puede modificar por seguridad. Contacta soporte si necesitas cambiarlo.
                </small>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Teléfono <span className={styles.required}>*</span></label>
                <div className={styles.inputWrap}>
                  <FiPhone className={styles.leadingIcon} />
                  <input
                    className={styles.field}
                    value={form.phone || ''}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+52 55 1234 5678"
                    autoComplete="tel"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.subsectionTitle}>
              <FiHome /> Información de Dirección
            </h3>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.label}>País <span className={styles.required}>*</span></label>
                <div className={styles.inputWrap}>
                  <FiFlag className={styles.leadingIcon} />
                  <input
                    className={styles.field}
                    value={form.country || ''}
                    onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                    placeholder="México"
                    autoComplete="country-name"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Estado <span className={styles.required}>*</span></label>
                <div className={styles.inputWrap}>
                  <FiMapPin className={styles.leadingIcon} />
                  <input
                    className={styles.field}
                    value={form.state || ''}
                    onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                    placeholder="Ciudad de México"
                    autoComplete="address-level1"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Ciudad <span className={styles.required}>*</span></label>
                <div className={styles.inputWrap}>
                  <FiMapPin className={styles.leadingIcon} />
                  <input
                    className={styles.field}
                    value={form.city || ''}
                    onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                    placeholder="CDMX"
                    autoComplete="address-level2"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Código Postal <span className={styles.required}>*</span></label>
                <div className={styles.inputWrap}>
                  <FiMapPin className={styles.leadingIcon} />
                  <input
                    className={styles.field}
                    value={form.postal_code || ''}
                    onChange={e => setForm(f => ({ ...f, postal_code: e.target.value }))}
                    placeholder="03100"
                    maxLength={5}
                    autoComplete="postal-code"
                  />
                </div>
              </div>

              <div className={styles.formGroupFull}>
                <label className={styles.label}>Dirección Completa <span className={styles.required}>*</span></label>
                <div className={styles.inputWrap}>
                  <FiMapPin className={styles.leadingIcon} />
                  <input
                    className={styles.field}
                    value={form.address || ''}
                    onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                    placeholder="Calle, número, colonia"
                    autoComplete="street-address"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.subsectionTitle}>
              <FiFileText /> Información Fiscal
            </h3>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.label}>RFC <span className={styles.required}>*</span></label>
                <div className={styles.inputWrap}>
                  <FiFileText className={styles.leadingIcon} />
                  <input
                    className={styles.field}
                    value={form.rfc || ''}
                    onChange={e => setForm(f => ({ ...f, rfc: e.target.value.toUpperCase() }))}
                    placeholder="ABCD123456XYZ"
                    maxLength={13}
                    autoComplete="off"
                  />
                </div>
                <small className={styles.hint}>Registro Federal de Contribuyentes (13 caracteres)</small>
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.subsectionTitle}>
              <FiShield /> Políticas y Consentimientos
            </h3>
            <div className={styles.policiesGrid}>
              <div className={styles.policyItem}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={privacyAccepted}
                    onChange={e => setPrivacyAccepted(e.target.checked)}
                  />
                  <span className={styles.checkmark}></span>
                  <span className={styles.policyText}>
                    Acepto el <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className={styles.policyLink}>Aviso de Privacidad</a> <span className={styles.required}>*</span>
                  </span>
                </label>
              </div>
              
              <div className={styles.policyItem}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={dataConsentAccepted}
                    onChange={e => setDataConsentAccepted(e.target.checked)}
                  />
                  <span className={styles.checkmark}></span>
                  <span className={styles.policyText}>
                    Doy mi consentimiento para el uso y resguardo de mis datos personales, validación de identidad y consultas ante proveedores externos <span className={styles.required}>*</span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {msg ? (
            <div className={styles.helperRow}>
              <p
                className={[
                  styles.message,
                  msg.type === 'success' ? styles.messageSuccess : '',
                  msg.type === 'error' ? styles.messageError : ''
                ].join(' ')}
              >
                {msg.text}
              </p>
            </div>
          ) : null}

          <button onClick={handleSave} disabled={saving} className={styles.saveButton} type="button">
            <FiSave /> {saving ? t('common.saving') : t('common.save_changes')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default ContactDataPage;
